import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useAnalytics } from '@bonusly/analytics';
import { ActionData } from '@bonusly/common-action-input';
import { createBonusData } from '@bonusly/common-give-box';
import { useHttpClient, type HttpResponse } from '@bonusly/http-client';
import { useIdentity } from '@bonusly/who-am-i';

import { Bonus, CachedBonuses } from '../../../../../../types/bonus';
import { CachedProfileBonuses, updateCachedComment, updateCommentInProfile } from '../../../cache/mutators/bonus';
import { QueryKey } from '../../../hooks/types';
import { BonusFeedTrackedAction } from '../../../hooks/use-bonus-feed-item-analytics';
import { useContextualizedBonusDetails } from '../../../hooks/use-contextualized-bonus-details';

interface UseUpdateCommentConfig {
	onError?: (error: unknown) => void;
	onSuccess?: VoidFunction;
}

export const useUpdateComment = (comment: Bonus, bonus: Bonus, { onError, onSuccess }: UseUpdateCommentConfig = {}) => {
	const { track } = useAnalytics();

	const queryClient = useQueryClient();
	const { isUserReceiver, isCreatedByUser } = useContextualizedBonusDetails(bonus);
	const http = useHttpClient('v2');
	const { refetchProfile } = useIdentity();

	const { mutate, isLoading: isSubmitting } = useMutation<HttpResponse<{ result: Bonus }>, unknown, ActionData, unknown>(
		(action) => {
			const data = {
				bonus: createBonusData({ action, bonus }),
			};
			// when patching, if the attachments is missing, the attachment_attributes is not sent
			// which the backend needs so understand to delete any attachments.
			if (!data.bonus.attachments_attributes) {
				data.bonus.attachments_attributes = [];
			}
			return http.patch('/bonuses/' + comment.id, data);
		},
		{
			onError: (err: unknown) => {
				onError?.(err);
			},
			onSuccess: ({ data: { result: comment } }) => {
				queryClient.setQueriesData<CachedBonuses>([QueryKey.BonusesInfinite], (cached) => updateCachedComment(comment, cached));
				queryClient.setQueriesData<CachedProfileBonuses>([QueryKey.ProfileBonuses], (cached) => updateCommentInProfile(comment, cached));

				track(BonusFeedTrackedAction.CommentEdit, {
					isUserGiver: isCreatedByUser,
					isUserReceiver,
				});

				// NOTE: refetch profile to update give box pill balances
				refetchProfile();

				onSuccess?.();
			},
		}
	);

	return {
		isSubmitting,
		onSubmit: mutate,
	};
};
